import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { getDeviceList } from '../../../store/devices/actionCreators';
import { Device } from '../../../store/devices/deviceTypes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink } from '@fortawesome/pro-light-svg-icons';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton } from '@mui/material';
import StatusChip from '../../Shared/StatusChip';
import { Status } from '../../../shared/constants';
import {
  faBarsFilter,
  faCircleExclamation,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import DashboardTableLegend from './DashboardTableLegend';
import CustomerCareDTable from '../../Shared/CustomerCareDTable';
import OverviewPopover from './OverviewPopover';
import NoDataCard from './NoDataCard';
import LoadingSpinner from '../../Shared/LoadingSpinner';
import { parseDateToLocal } from '../../../shared/utils';
import { ReactComponent as MailIcon } from '../../../images/icons/mail.svg';
import { ReactComponent as CallIcon } from '../../../images/icons/call.svg';
import {
  customError,
  dimGrey,
  warningBg,
  warningYellow,
} from '../../../mainTheme';

interface HubsDetailPanelProps {
  selectedTab: number;
  isFacilityAdmin: boolean;
}

interface ActionCellProps {
  isUnassigned: boolean;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
}

const ActionCell = ({
  isUnassigned,
  contactName,
  contactPhone,
  contactEmail,
}: ActionCellProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleEmailClick = () => {
    if (contactEmail) {
      window.location.href = `mailto:${contactEmail}`;
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      borderRadius="8px"
      border="1px solid var(--Outline-O5---Separator, #D4D4D9)"
    >
      {isUnassigned ? (
        <Box width="100%" display="flex" justifyContent="center">
          <IconButton
            sx={{
              marginX: '4px',
              width: 32,
              height: 32,
              p: 0,
            }}
          >
            <FontAwesomeIcon
              icon={faLink}
              style={{ width: 20, height: 20, color: dimGrey }}
            />
          </IconButton>
        </Box>
      ) : (
        <>
          <IconButton
            size="small"
            onClick={handleOpenModal}
            sx={{
              marginX: '4px',
              width: 32,
              height: 32,
              p: 0,
            }}
          >
            <CallIcon style={{ width: 20, height: 20 }} />
          </IconButton>
          <Box
            width="1px"
            height="32px"
            bgcolor="var(--Outline-O5---Separator, #D4D4D9)"
          />
          <IconButton
            size="small"
            onClick={handleEmailClick}
            sx={{
              marginX: '4px',
              width: 32,
              height: 32,
              borderRadius: '12px',
              p: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MailIcon style={{ width: 20, height: 20 }} />
          </IconButton>
        </>
      )}
      {isModalOpen && (
        <OverviewPopover
          onClose={handleCloseModal}
          contactName={contactName}
          contactPhone={contactPhone}
        />
      )}
    </Box>
  );
};
const stateSelector = (state: RootState) => {
  const { devices } = state;
  const { deviceList, isLoading: isDeviceListLoading } = devices;

  return {
    deviceList,
    isDeviceListLoading,
  };
};

const HubsDetailPanel: React.FC<HubsDetailPanelProps> = ({
  selectedTab,
  isFacilityAdmin,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { deviceList, isDeviceListLoading } = useSelector<
    RootState,
    ReturnType<typeof stateSelector>
  >(stateSelector);

  useEffect(() => {
    if (deviceList == null) {
      dispatch(getDeviceList());
    }
  }, [deviceList, dispatch]);

  const onViewAll = () => {
    navigate(`/v2/hubs`);
  };

  const columns = [
    {
      field: 'patient',
      headerName: 'Patient',
      flex: 1,
    },
    ...(selectedTab !== 2
      ? [
          {
            field: 'facility_name',
            headerName: isFacilityAdmin ? 'Facility location' : 'Facility',
            flex: 1,
          },
        ]
      : []),
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      sortComparator: (v1: any, v2: any) => {
        return v1.text.localeCompare(v2.text);
      },
      renderCell: (params: any) => {
        const statusValue = params.value;
        return (
          <Box display="flex" alignItems="center">
            <StatusChip
              icon={statusValue.icon}
              iconColor={statusValue.color}
              labelText={statusValue.text}
              conditionalValue={statusValue.text === Status.Unassigned}
              backgroundColor={warningBg}
            />
          </Box>
        );
      },
    },
    { field: 'hub_serial_number', headerName: 'Hub serial number', flex: 1 },
    { field: 'last_seen_online', headerName: 'Last online', flex: 1 },
    {
      field: 'action',
      headerName: 'Actions',
      renderCell: (params: any) => (
        <ActionCell
          isUnassigned={params.row.isUnassigned}
          contactName={params.row.contact_name}
          contactPhone={params.row.contact_phone}
          contactEmail={params.row.contact_email}
        />
      ),
    },
  ];

  const rows: any[] = useMemo(() => {
    let filteredDevices = deviceList;

    if (deviceList) {
      filteredDevices = deviceList.filter(
        (device: Device) =>
          device.status === Status.Offline.toLowerCase() ||
          device.customer_id === null,
      );

      if (selectedTab === 1) {
        filteredDevices = filteredDevices.filter(
          (device: Device) => device.facility_id != null,
        );
      } else if (selectedTab === 2) {
        filteredDevices = filteredDevices.filter(
          (device: Device) => device.facility_id == null,
        );
      }
    }

    return (
      filteredDevices?.map((device: Device) => {
        const isUnassigned = device.customer_id === null;
        const hasPatient = device?.first_name || device?.last_name;

        // Adapt action values based on Role
        const contactName = isFacilityAdmin
          ? `${device?.first_name ?? ''} ${device?.last_name ?? ''}`.trim()
          : device.contact_name || null;
        const contactPhone = isFacilityAdmin
          ? device.phone || null
          : device.contact_phone || null;
        const contactEmail = isFacilityAdmin
          ? device.email || null
          : device.contact_email || null;

        return {
          id: device.id,
          patient: hasPatient
            ? `${device?.first_name} ${device?.last_name}`
            : 'N/A',
          facility_name: device.facility_name || 'N/A',
          status: {
            icon: isUnassigned ? faCircleExclamation : faTriangleExclamation,
            color: isUnassigned ? warningYellow : customError,
            text: isUnassigned ? Status.Unassigned : Status.Offline,
          },
          hub_serial_number: device?.serial_number,
          last_seen_online: device.last_seen_online
            ? parseDateToLocal(device.last_seen_online, 'yyyy-MM-dd HH:mm:ss')
            : 'N/A',
          action: faBarsFilter,
          isUnassigned: isUnassigned,
          contact_name: contactName,
          contact_phone: contactPhone,
          contact_email: contactEmail,
        };
      }) ?? []
    );
  }, [deviceList, selectedTab]);

  const tableLegendData = [
    {
      name: 'Offline',
      color: customError,
    },
    {
      name: 'Unassigned',
      color: warningYellow,
    },
  ];
  if (isDeviceListLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {rows.length ? (
        <Box p={2}>
          <DashboardTableLegend tableLegendData={tableLegendData} />
          <CustomerCareDTable
            rows={rows}
            columns={columns}
            loading={isDeviceListLoading}
            pageSize={5}
            onViewAll={onViewAll}
            defaultSortField="status"
            defaultSortDirection="asc"
          />
        </Box>
      ) : (
        <NoDataCard />
      )}
    </>
  );
};

export default HubsDetailPanel;
